import React, { useEffect, useState } from 'react';
import { supabase } from '../App';

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-12');
            entry.target.classList.add('opacity-100', 'translate-y-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const hiddenElements = document.querySelectorAll('.animate-on-scroll');
    hiddenElements.forEach((el) => observer.observe(el));

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', msg: '' });

    try {
      const { error } = await supabase
        .from('contact_messages')
        .insert([
          { 
            sender_name: formData.name, 
            sender_email: formData.email, 
            message_body: formData.message 
          }
        ]);

      if (error) throw error;

      setStatus({ type: 'success', msg: 'Message securely transmitted. Our team will be in touch shortly.' });
      setFormData({ name: '', email: '', message: '' });
      
      setTimeout(() => setStatus({ type: '', msg: '' }), 5000);
      
    } catch (error) {
      console.error('Submission Error:', error.message);
      setStatus({ type: 'error', msg: 'System error processing request. Please try again or contact us directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-6">
      <div className="max-w-6xl mx-auto">
        
        <div className="relative overflow-hidden rounded-2xl bg-slate-900/60 backdrop-blur-md border border-slate-800 p-8 md:p-12 mb-10 shadow-lg animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out">
          <div className="absolute inset-0 z-0">
            <img src="/assets/images/background01.png" className="w-full h-full object-cover grayscale opacity-[0.05]" alt="Contact" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/50 to-transparent"></div>
          </div>
          <div className="relative z-10">
            <p className="text-blue-500 text-xs uppercase tracking-[0.2em] font-bold mb-2">Get in touch with our team</p>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tighter">Contact Us</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          <div 
            className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-2xl p-4 md:p-10 flex flex-col gap-6 shadow-lg animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out"
            style={{ transitionDelay: '150ms' }}
          >
            <div className="flex items-start gap-4 group">
              <div className="text-slate-500 mt-1 group-hover:text-blue-400 transition-colors duration-300">
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              </div>
              <div>
                {/* Updated to the new GoDaddy email address and made clickable */}
                <a href="mailto:andymthembu@ilamitechnologies.com" className="text-slate-300 text-sm font-medium group-hover:text-white transition-colors duration-300">
                  info@ilamitechnologies.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="text-slate-500 mt-1 group-hover:text-blue-400 transition-colors duration-300">
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
              </div>
              <div>
                <p className="text-slate-300 text-sm font-medium group-hover:text-white transition-colors duration-300">+27 73 255 4298</p>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="text-slate-500 mt-1 group-hover:text-blue-400 transition-colors duration-300">
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              </div>
              <div>
                <p className="text-slate-300 text-sm font-medium leading-relaxed group-hover:text-white transition-colors duration-300">
                  30 Acutt Street, Durban Central, <br />4001, South Africa
                </p>
              </div>
            </div>

            <div className="w-full h-48 bg-slate-900 rounded-lg mt-4 overflow-hidden border border-slate-800 shadow-inner group relative">
              <iframe 
                title="ilami Technologies Location"
                src="https://maps.google.com/maps?q=30%20Acutt%20Street,%20Durban%20Central,%204001,%20South%20Africa&t=&z=15&ie=UTF8&iwloc=&output=embed" 
                className="w-full h-full opacity-70 grayscale contrast-125 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 ease-in-out"
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          <div 
            className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-2xl p-8 md:p-10 shadow-lg flex flex-col justify-center animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out"
            style={{ transitionDelay: '300ms' }}
          >
            <h3 className="text-2xl font-bold text-white mb-8 text-center tracking-tight">Send a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {status.msg && (
                <div className={`p-4 rounded-lg text-sm font-medium border ${status.type === 'success' ? 'bg-blue-900/30 text-blue-400 border-blue-800' : 'bg-red-900/30 text-red-400 border-red-800'} transition-all duration-300`}>
                  {status.msg}
                </div>
              )}

              <div>
                <label className="block text-slate-400 text-xs font-bold mb-2 ml-1 uppercase tracking-wider">Name</label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full bg-slate-950/80 border border-slate-800 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all shadow-inner disabled:opacity-50" 
                />
              </div>
              <div>
                <label className="block text-slate-400 text-xs font-bold mb-2 ml-1 uppercase tracking-wider">Email</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full bg-slate-950/80 border border-slate-800 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all shadow-inner disabled:opacity-50" 
                />
              </div>
              <div>
                <label className="block text-slate-400 text-xs font-bold mb-2 ml-1 uppercase tracking-wider">Message</label>
                <textarea 
                  rows="4" 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full bg-slate-950/80 border border-slate-800 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none shadow-inner disabled:opacity-50"
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] active:scale-95 transition-all duration-300 text-sm mt-4 tracking-widest uppercase flex justify-center items-center gap-2 disabled:opacity-70 disabled:pointer-events-none"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Transmitting...
                  </>
                ) : 'Send Message'}
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Contact;